// Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
// See LICENSE in the project root for license information.
import graphStyles from '../styles/graphView.module.css';
export function createGraphSelectionController(options) {
    let graphMarqueeEl = null;
    let dragSelecting = false;
    let dragStart = null;
    let dragLast = null;
    let preDragSelection = null;
    let dragModifierMode = 'replace';
    const _selectionChanged = () => {
        options.onSelectionChanged();
    };
    const _graphPointFromEvent = (e) => {
        const rect = options.graphEl.getBoundingClientRect();
        return {
            x: e.clientX - rect.left + options.graphEl.scrollLeft,
            y: e.clientY - rect.top + options.graphEl.scrollTop
        };
    };
    const _updateDragModifierMode = (e) => {
        if (e.altKey)
            dragModifierMode = 'subtract';
        else if (e.metaKey || e.ctrlKey || e.shiftKey)
            dragModifierMode = 'add';
        else
            dragModifierMode = 'replace';
    };
    const _updateMarquee = () => {
        if (!dragSelecting || !graphMarqueeEl || !dragStart || !dragLast)
            return;
        const x1 = Math.min(dragStart.x, dragLast.x);
        const y1 = Math.min(dragStart.y, dragLast.y);
        const x2 = Math.max(dragStart.x, dragLast.x);
        const y2 = Math.max(dragStart.y, dragLast.y);
        graphMarqueeEl.style.left = x1 + 'px';
        graphMarqueeEl.style.top = y1 + 'px';
        graphMarqueeEl.style.width = x2 - x1 + 'px';
        graphMarqueeEl.style.height = y2 - y1 + 'px';
        const newlySelected = new Set();
        for (const [name, pos] of options.getGraphNodePositions().entries()) {
            const nx1 = pos.x;
            const ny1 = pos.y;
            const nx2 = pos.x + options.graphNodeWidth;
            const ny2 = pos.y + options.graphNodeHeight;
            if (nx2 < x1 || nx1 > x2 || ny2 < y1 || ny1 > y2)
                continue;
            newlySelected.add(name);
        }
        let nextSelection;
        if (dragModifierMode === 'add') {
            nextSelection = new Set(preDragSelection || []);
            newlySelected.forEach((name) => nextSelection.add(name));
        }
        else if (dragModifierMode === 'subtract') {
            nextSelection = new Set(preDragSelection || []);
            newlySelected.forEach((name) => nextSelection.delete(name));
        }
        else {
            nextSelection = newlySelected;
        }
        options.setSelection(nextSelection);
        options.onLiveSelectionChanged();
    };
    const _beginDragSelection = (e) => {
        if (options.getCurrentView() !== 'graph' || e.button !== 0)
            return;
        const target = e.target;
        if (target && target.closest && target.closest(`.${graphStyles.opNode}`))
            return;
        dragSelecting = true;
        dragStart = _graphPointFromEvent(e);
        dragLast = dragStart;
        preDragSelection = new Set(options.getSelection());
        graphMarqueeEl = document.createElement('div');
        graphMarqueeEl.className = graphStyles.graphMarquee;
        options.graphEl.appendChild(graphMarqueeEl);
        _updateMarquee();
        e.preventDefault();
    };
    const _wireGraphMarqueeSelection = () => {
        options.graphEl.addEventListener('mousedown', (e) => {
            _updateDragModifierMode(e);
            _beginDragSelection(e);
        });
        options.graphEl.addEventListener('mousemove', (e) => {
            if (!dragSelecting)
                return;
            dragLast = _graphPointFromEvent(e);
            _updateDragModifierMode(e);
            _updateMarquee();
            e.preventDefault();
        });
        window.addEventListener('mouseup', () => {
            if (!dragSelecting)
                return;
            dragSelecting = false;
            if (graphMarqueeEl) {
                graphMarqueeEl.remove();
                graphMarqueeEl = null;
            }
            dragStart = null;
            dragLast = null;
            preDragSelection = null;
        });
        window.addEventListener('keydown', (e) => {
            if (dragSelecting) {
                _updateDragModifierMode(e);
                _updateMarquee();
            }
        });
        window.addEventListener('keyup', (e) => {
            if (dragSelecting) {
                _updateDragModifierMode(e);
                _updateMarquee();
            }
        });
    };
    const _singleSelect = (name) => {
        options.setSelection(new Set([name]));
        _selectionChanged();
    };
    const _toggleSelect = (name) => {
        const nextSelection = new Set(options.getSelection());
        if (nextSelection.has(name))
            nextSelection.delete(name);
        else
            nextSelection.add(name);
        options.setSelection(nextSelection);
        _selectionChanged();
    };
    const _expandSelectionDependencies = () => {
        const currentSelection = options.getSelection();
        if (!currentSelection.size)
            return;
        const queue = [...currentSelection];
        const seen = new Set(currentSelection);
        const operations = options.getOperations();
        while (queue.length) {
            const name = queue.shift();
            if (!name)
                continue;
            const op = operations.get(name);
            if (!op)
                continue;
            for (const dep of op.dependencies || []) {
                if (!seen.has(dep) && operations.has(dep)) {
                    seen.add(dep);
                    queue.push(dep);
                }
            }
        }
        if (seen.size !== currentSelection.size) {
            options.setSelection(seen);
            _selectionChanged();
        }
    };
    const _expandSelectionConsumers = () => {
        const currentSelection = options.getSelection();
        if (!currentSelection.size)
            return;
        const operations = options.getOperations();
        const dependents = new Map();
        for (const op of operations.values()) {
            for (const dep of op.dependencies || []) {
                if (!operations.has(dep))
                    continue;
                let setForDep = dependents.get(dep);
                if (!setForDep) {
                    setForDep = new Set();
                    dependents.set(dep, setForDep);
                }
                setForDep.add(op.name);
            }
        }
        const queue = [...currentSelection];
        const seen = new Set(currentSelection);
        while (queue.length) {
            const name = queue.shift();
            if (!name)
                continue;
            const consumers = dependents.get(name);
            if (!consumers)
                continue;
            for (const consumer of consumers) {
                if (!seen.has(consumer)) {
                    seen.add(consumer);
                    queue.push(consumer);
                }
            }
        }
        if (seen.size !== currentSelection.size) {
            options.setSelection(seen);
            _selectionChanged();
        }
    };
    return {
        singleSelect: _singleSelect,
        toggleSelect: _toggleSelect,
        expandSelectionDependencies: _expandSelectionDependencies,
        expandSelectionConsumers: _expandSelectionConsumers,
        wireGraphMarqueeSelection: _wireGraphMarqueeSelection
    };
}
//# sourceMappingURL=graphSelection.js.map